import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaWhatsapp } from 'react-icons/fa';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import WhatsAppFloat from '../../components/WhatsAppFloat';
import Icon from '../../components/AppIcon';
import GameCard from './components/GameCard';
import CategoryFilter from './components/CategoryFilter';
import GamePreviewModal from './components/GamePreviewModal';
import FeaturedGamesCarousel from './components/FeaturedGamesCarousel';
import LiveStatsBar from './components/LiveStatsBar';
import { CATEGORIES, GAMES } from '../../data/games';
import { SITE, WA_MESSAGES, WHATSAPP_DISPLAY, openWhatsApp } from '../../config/site';

const slug = (category) => category?.toLowerCase()?.replace(/\s+/g, '-');

const GamesShowcaseGamingPalace = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedGame, setSelectedGame] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popularity');
  const [viewMode, setViewMode] = useState('grid');

  const categories = useMemo(
    () =>
      CATEGORIES.map((c) => ({
        ...c,
        count: c.id === 'all' ? GAMES.length : GAMES.filter((g) => slug(g.category) === c.id).length,
      })),
    []
  );

  const featuredGames = useMemo(() => GAMES.filter((g) => g.isPopular).slice(0, 4), []);

  const visibleGames = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = GAMES.filter((g) => {
      const matchesCategory = activeCategory === 'all' || slug(g.category) === activeCategory;
      const matchesSearch =
        !q ||
        g.name.toLowerCase().includes(q) ||
        g.category.toLowerCase().includes(q) ||
        g.features.some((f) => f.toLowerCase().includes(q));
      return matchesCategory && matchesSearch;
    });

    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return b.rating - a.rating;
        case 'minBet':
          return a.minBet - b.minBet;
        case 'maxWin':
          return b.maxWin - a.maxWin;
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return b.playerCount - a.playerCount;
      }
    });
  }, [activeCategory, searchQuery, sortBy]);

  const handleWhatsAppClick = (game = null) => {
    openWhatsApp(game ? WA_MESSAGES.game(game.name) : WA_MESSAGES.general);
  };

  const handlePreviewClick = (game) => {
    setSelectedGame(game);
    setIsPreviewOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        {/* Deindexed: keep site reachable directly, but out of search results. */}
        <meta name="robots" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
        <meta name="googlebot" content="noindex, nofollow, noarchive, nosnippet, noimageindex" />
        <title>Games Showcase - Gaming Palace | Nitin Online Book | Nitin Online Book</title>
        <meta
          name="description"
          content="Explore premium gaming options at Nitin Online Book and Nitin Online Book. Live casino games, sports betting, slots, instant win and lottery with expert WhatsApp support and instant access. Nitin online book games for the best betting experience."
        />
        <meta
          name="keywords"
          content="Nitin Online Book games, Nitin Online Book games, online casino, live games, sports betting, slots, lottery, teen patti, andar bahar, cricket betting, WhatsApp gaming, Nitin online book casino, Nitin betting games"
        />
        <meta property="og:title" content="Games Showcase - Gaming Palace | Nitin Online Book | Nitin Online Book" />
        <meta
          property="og:description"
          content="Explore premium gaming options at Nitin Online Book and Nitin Online Book. Live casino games, sports betting, slots, and lottery with expert WhatsApp support and instant access."
        />
        <meta property="og:image" content={`${SITE.url}/assets/images/gen/og-cover.svg`} />
        <meta property="og:url" content={`${SITE.url}/games-showcase-gaming-palace`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE.name} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Games Showcase - Gaming Palace | Nitin Online Book | Nitin Online Book" />
        <meta
          name="twitter:description"
          content="Live casino, cricket and football books, instant win games and lottery draws — all with 24/7 WhatsApp support from Nitin Online Book."
        />
        <meta name="twitter:image" content={`${SITE.url}/assets/images/gen/og-cover.svg`} />
        <link rel="canonical" href={`${SITE.url}/games-showcase-gaming-palace`} />

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE.url}/` },
              { '@type': 'ListItem', position: 2, name: 'Games', item: `${SITE.url}/games-showcase-gaming-palace` },
            ],
          })}
        </script>
      </Helmet>

      <Header />
      <LiveStatsBar />

      <main className="relative mx-auto max-w-7xl overflow-hidden px-4 py-14 sm:px-6 lg:px-8">
        <span className="aurora left-[-10%] top-[4%] h-96 w-96 bg-royal-violet/25" aria-hidden="true" />
        <span className="aurora right-[-10%] top-[40%] h-96 w-96 bg-royal-gold/20" aria-hidden="true" />

        {/* Page head */}
        <div className="relative mx-auto max-w-3xl text-center">
          <span className="badge-pill"><Icon name="Gamepad2" size={13} /> Gaming palace</span>
          <h1 className="mt-6 font-heading text-hero font-bold text-white">
            Every table in the <span className="gold-text">palace</span>
          </h1>
          <p className="mt-4 text-body-lg text-white/60">
            Live casino floors, cricket and football books, instant-win rounds and daily draws — all audited, all
            open now, all bookable in one WhatsApp message to {WHATSAPP_DISPLAY}.
          </p>
        </div>

        {/* Featured */}
        <div className="relative mt-12">
          <FeaturedGamesCarousel
            featuredGames={featuredGames}
            onWhatsAppClick={handleWhatsAppClick}
            onPreviewClick={handlePreviewClick}
          />
        </div>

        {/* Controls */}
        <div className="card-flat relative mt-8 p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full max-w-md">
              <Icon name="Search" size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/35" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search Teen Patti, cricket, roulette…"
                className="h-12 w-full rounded-full border border-white/10 bg-white/[0.05] pl-11 pr-4 text-sm text-white placeholder:text-white/35 focus:border-royal-gold/60 focus:outline-none focus:ring-0"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Icon name="ArrowUpDown" size={15} className="text-white/40" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="h-11 rounded-full border border-white/10 bg-[#12121F] px-4 text-sm text-white focus:border-royal-gold/60 focus:outline-none focus:ring-0"
                >
                  <option value="popularity">Most popular</option>
                  <option value="rating">Highest rated</option>
                  <option value="minBet">Lowest min bet</option>
                  <option value="maxWin">Biggest max win</option>
                  <option value="name">Name A–Z</option>
                </select>
              </div>

              <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.05] p-1">
                {[
                  { id: 'grid', icon: 'LayoutGrid' },
                  { id: 'list', icon: 'List' },
                ].map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setViewMode(v.id)}
                    aria-label={`${v.id} view`}
                    className={`rounded-full p-2.5 transition ${
                      viewMode === v.id ? 'bg-gradient-royal text-[#150F02]' : 'text-white/45 hover:text-white'
                    }`}
                  >
                    <Icon name={v.icon} size={16} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="relative mt-6">
          <CategoryFilter
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={(id) => setActiveCategory(id)}
            gameCount={visibleGames.length}
          />
        </div>

        {/* Grid */}
        <div className="perspective relative mt-8">
          {visibleGames.length > 0 ? (
            <div className={viewMode === 'grid' ? 'grid gap-6 sm:grid-cols-2 xl:grid-cols-3' : 'space-y-4'}>
              {visibleGames.map((game, i) => (
                <div key={game.id} className="animate-rise h-full" style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }}>
                  <GameCard
                    game={game}
                    layout={viewMode}
                    onWhatsAppClick={handleWhatsAppClick}
                    onPreviewClick={handlePreviewClick}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="card-flat py-16 text-center">
              <Icon name="SearchX" size={44} className="mx-auto text-white/25" />
              <h3 className="mt-4 font-heading text-xl font-bold text-white">No tables match that search</h3>
              <p className="mt-2 text-sm text-white/50">Try another game name, or ask the desk to open a private table.</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('all');
                }}
                className="btn-3d btn-3d-ghost mt-6 !py-2.5 text-sm"
              >
                <Icon name="RotateCcw" size={15} /> Reset filters
              </button>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="panel-gold shine-hover relative mt-14 overflow-hidden px-6 py-12 text-center sm:px-12">
          <span className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-royal-gold/15">
            <Icon name="Crown" size={30} color="#F0C24B" />
          </span>
          <h2 className="font-heading text-3xl font-bold text-white sm:text-4xl">
            Ready to take your <span className="gold-text">seat</span>?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-white/60">
            Thousands of players run their gaming through Nitin Online Book because the answers are instant and the
            payouts are faster. Tell us which table you want — everything else is handled for you.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button onClick={() => handleWhatsAppClick()} className="btn-3d btn-3d-wa">
              <FaWhatsapp size={19} /> Start on WhatsApp
            </button>
            <button onClick={() => openWhatsApp(WA_MESSAGES.vip)} className="btn-3d btn-3d-ghost">
              <Icon name="Crown" size={17} /> Ask about VIP limits
            </button>
          </div>
          <p className="mt-5 text-xs text-white/40">Available 24/7 · instant setup · expert guidance · 18+ only</p>
        </div>
      </main>

      <GamePreviewModal
        game={selectedGame}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        onWhatsAppClick={handleWhatsAppClick}
      />

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default GamesShowcaseGamingPalace;
